import { get, isEmpty } from 'lodash';
import { createAPIService } from './api.service';
import { moveFileToVault } from './document.api';
import errors, { createAPIError, defaultAPIError } from './errors';
import { getUtm } from 'utils/utm';
import { mapValuesAndFields } from 'utils/form.utils';
import { referenceType } from 'common/models';
import { logApiError } from 'utils/bugsnagHelper';

// expose endpoints for less brittle tests
export const ECARD_ENDPOINT = '/card/fulfillment/ecard';
export const ENROLL_ENDPOINT = '/v2/enrollments';
export const FULFILLMENT_ENDPOINT = '/card/fulfillment';
export const FIND_ENROLLMENTS_ENDPOINT = '/card/findEnrollments';
export const CHECK_UCI_ENDPOINT = uci => `/card/${uci}/enrollment`;

const service = createAPIService('/services/patient');

// endpoints
service.getECard = async (language, programId) => {
  const response = await service.post(
    ECARD_ENDPOINT,
    {
      language,
      program: programId,
      utm: getUtm(),
    },
    { responseType: 'blob' }
  );

  const filename = (response.headers['content-disposition'] || '').match(
    /filename="(.*)"/
  );
  return {
    filename: filename && filename.length ? filename[1] : undefined,
    data: response.data,
  };
};

service.enroll = async (data, pathMap, processData = true) => {
  try {
    const response = await service.post(
      ENROLL_ENDPOINT,
      processData ? mapValuesAndFields(data, pathMap) : data
    );

    if (get(response, 'data.error', false)) {
      logApiError(response.data.error, 'failed enrollment');
      throw defaultAPIError(response.data.error, errors.ENROLLMENT_FAILED);
    }

    return response.data;
  } catch (err) {
    logApiError(err, 'failed enrollment');
    throw defaultAPIError(err, errors.ENROLLMENT_FAILED);
  }
};

service.checkUCI = async data => {
  try {
    const response = await service.post(
      CHECK_UCI_ENDPOINT(data.clientId || data.benefit.card.uci)
    );
    if (response.data.enrollmentExists) {
      throw createAPIError(errors.ENROLLMENT_EXISTS_FOR_CLIENTID);
    }
    return true;
  } catch (err) {
    switch (get(err, 'response.status', err.code)) {
      case 404:
      case 403:
        throw createAPIError(errors.UCI_INVALID);
      default:
        throw defaultAPIError(err);
    }
  }
};

service.fulfillment = async ({ preferredLanguage, programId, clientId }) => {
  try {
    const response = await service.post(FULFILLMENT_ENDPOINT, {
      language: preferredLanguage,
      uci: clientId,
      program: programId,
      utm: getUtm(),
    });
    return response;
  } catch (err) {
    throw defaultAPIError(err);
  }
};

service.findEnrollments = async (data, configurations, params) => {
  if (!get(data, 'disableDuplicateCheck', false)) {
    try {
      const requestPayload = {
        programId:
          params.length > 0
            ? params
            : get(data, 'benefit.program.programId', null),
        dob: get(data, 'patient.person.dateOfBirth', null),
        firstName: get(data, 'patient.person.contactInfo.firstName', null),
        lastName: get(data, 'patient.person.contactInfo.lastName', null),
      };
      const response = await service.post(
        FIND_ENROLLMENTS_ENDPOINT,
        requestPayload
      );
      if (response.data && get(response, 'data.enrolmentFound')) {
        throw createAPIError(errors.ENROLLMENT_EXISTS_FOR_PATIENT);
      }
      return false;
    } catch (err) {
      throw defaultAPIError(err);
    }
  } else return false;
};

service.moveFileToVault = async (enrollmentUuid, signatureIds = []) => {
  if (!isEmpty(signatureIds)) {
    const filePayload = {
      ids: signatureIds,
      references: [
        {
          referenceId: enrollmentUuid,
          referenceType: referenceType.ENROLMENT,
        },
      ],
    };
    try {
      await moveFileToVault(filePayload);
      return true;
    } catch (err) {
      logApiError(err, 'failed moving file to vault');
      return false;
    }
  }
};

export default service;
